import React, { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { SoftphoneSipConfig } from "../hooks/useSoftphoneConfig";
import { useTranslation } from "../i18n/LocaleContext";
import heroIcon from "../assets/hero-icon.png";

interface HeroCardProps {
  sipConfig: SoftphoneSipConfig | null;
  /** Versão servida no primeiro polling (useVersionCheck); `null` até a primeira resposta. Uma
   *  versão nova não tem UI: o SoftphoneWorkspace recarrega sozinho com o softphone ocioso. */
  currentVersion?: string | null;
  /** Administrador do tenant: só para ele existe o botão da tela de administração de usuários. */
  isTenantAdmin?: boolean;
  /** Vista de administração aberta: o botão vira "voltar". */
  tenantAdminActive?: boolean;
  onToggleTenantAdmin?: () => void;
}

const HeroCard: React.FC<HeroCardProps> = ({
  sipConfig,
  currentVersion = null,
  isTenantAdmin = false,
  tenantAdminActive = false,
  onToggleTenantAdmin,
}) => {
  const { t } = useTranslation();
  // Só tem efeito ≤720px, onde o hero colapsa numa linha e o .hero-meta vira detalhe sob
  // demanda. Acima do breakpoint a CSS mostra tudo e ignora este estado.
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <section className="hero-card" data-expanded={detailsOpen ? "true" : "false"}>
      <div className="hero-content">
        <p className="eyebrow">{t("hero.eyebrow")}</p>
        <div className="hero-title-row">
          <span className="hero-icon-chip">
            <img src={heroIcon} alt="" className="hero-icon" />
          </span>
          <h1>{t("hero.title")}</h1>
          <p className="hero-copy">
            {t("hero.copy")}
          </p>
          <span className="hero-compact-ext">{sipConfig?.extension || "—"}</span>
          <IconButton
            className="hero-toggle"
            size="small"
            aria-expanded={detailsOpen}
            aria-label={t("hero.toggleDetails")}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {/* Filho direto do .hero-card, e não da linha do título: é o que lhe dá uma coluna própria
          entre o conteúdo e o .hero-meta, encostando na borda esquerda da caixa de identificação.
          Dentro da linha do título ele seguiria o fluxo do texto e poderia cair na segunda linha,
          sob o `hero-copy`.

          Classe própria, e não `hero-toggle`: aquela é escondida acima de 720px (ela abre o
          detalhe do hero, que no desktop já está visível), e este botão vale nos dois tamanhos. */}
      {isTenantAdmin && onToggleTenantAdmin && (
        <Tooltip title={t(tenantAdminActive ? "tenantAdmin.back" : "hero.tenantAdmin")}>
          <IconButton
            className="hero-admin-toggle"
            size="small"
            aria-label={t(tenantAdminActive ? "tenantAdmin.back" : "hero.tenantAdmin")}
            aria-pressed={tenantAdminActive}
            onClick={onToggleTenantAdmin}
          >
            {/* `medium` (1.5rem) e não `small` (1.25rem): o botão fica sozinho na coluna, sem
                rótulo ao lado, então precisa de um pouco mais de peso visual que os ícones que
                acompanham texto. Os dois estados usam o mesmo tamanho — senão o botão mudaria de
                dimensão ao alternar. */}
            {tenantAdminActive ? <ArrowBackIcon fontSize="medium" /> : <ManageAccountsIcon fontSize="medium" />}
          </IconButton>
        </Tooltip>
      )}
      <div className="hero-meta">
        <div className="hero-meta-item">
          <span>{t("hero.user")}</span>
          <strong>{sipConfig?.displayName || sipConfig?.extension || "—"}</strong>
        </div>
        <div className="hero-meta-item">
          <span>{t("hero.extension")}</span>
          <strong>{sipConfig?.extension || "—"}</strong>
        </div>
        <div className="hero-meta-item">
          <span>{t("hero.sipDomain")}</span>
          <strong>{sipConfig?.domain || "—"}</strong>
        </div>
        <div className="hero-meta-item">
          <span>{t("hero.version")}</span>
          <strong>{currentVersion || "—"}</strong>
        </div>
      </div>
    </section>
  );
};

export default HeroCard;
